import { useEffect } from "react";
import SegmentButton from "src/components/segmentButton";
import styles from './styles.module.scss';
import cx from 'classnames';
import SwitchButton from "src/components/switchButton";
import { StreamDigestChannelsEnum, streamDigestChannels, TransactionalChannelsEnum, DigestTriggersEnum, TransactionalTriggersLabels, transactionalChannels, TransactionalTriggersEnum } from "src/types/settings/notifications";
import { useNotificationsFetch, useNotificationsUpdate } from "src/query/settings/notifications";
import _ from 'lodash';
import { toast } from "react-toastify";
import Toast from "src/components/toast";

interface TransactionalCardsProps {
    title: string,
    subTitle: string,
    classnames?: string,
    children?: React.ReactNode
    handleClick?: () => void,
}

interface TransactionalHeaderProps {
    title: string,
    subTitle: string,
    classnames?: string,
}

const Notifications = () => {
    const { data: notifications, isLoading: isLoadingNotifications, isFetching: isNotificationsFetching,  } = useNotificationsFetch();
    const { mutate: notificationsMutate, status: isNotificationStatus, isPending: updateNotificationPending, } = useNotificationsUpdate();
    
     // Method 
     useEffect(() => {
        if(isNotificationStatus === 'success'){
            toast(
              <Toast.Sucess
                  message="Updated sucessfully"
               />, {
               autoClose: 1000,
          })
        } else if(isNotificationStatus === 'error') {
            toast(
              <Toast.Failure
                   message={`Error in updating`}
               />, {
               autoClose: 1000,
            });
        }
     },[isNotificationStatus]);

     const selectedStreamDigestChannel = () => { // selected Stream Digest Channel
        const { followerEmailDigestEnabled, weekEmailDigestEnabled } = notifications;

        let selectedChannel;

        if (followerEmailDigestEnabled && weekEmailDigestEnabled) { // both
            selectedChannel = StreamDigestChannelsEnum.BOTH;
        } else {
            if (followerEmailDigestEnabled) { // daily
                selectedChannel = StreamDigestChannelsEnum.DAILY;
            }
            if (weekEmailDigestEnabled) { // weekly
                selectedChannel = StreamDigestChannelsEnum.WEEKLY;
            }
        }

        return selectedChannel ?? undefined;
     };

    const selectedTransactionalChannel = (transactionalTrigger: TransactionalTriggersEnum) => { // selected Transactional Channel
        const {email, push } = notifications;

        let selectedChannel;

        if (email[transactionalTrigger] && push[transactionalTrigger]) { // both notification
            selectedChannel = TransactionalChannelsEnum.BOTH;
        } else {
            if (email[transactionalTrigger]) { // email notification
                selectedChannel = TransactionalChannelsEnum.EMAIL;
            }
            if (push[transactionalTrigger]) { // push notification  
                selectedChannel = TransactionalChannelsEnum.PUSH;
            }
        }

        return selectedChannel;
    };

    // updated the digest settings
    const getUpdatedDigestSettings = (channel: StreamDigestChannelsEnum | DigestTriggersEnum) => {
        const daily = notifications[DigestTriggersEnum.followerEmailDigestEnabled];
        const weekly = notifications[DigestTriggersEnum.weekEmailDigestEnabled];
        const bothActive = daily && weekly;

        switch (channel) {
                case StreamDigestChannelsEnum.DAILY: // stream daily notification
                    return {
                    [DigestTriggersEnum.followerEmailDigestEnabled]: bothActive ? true : !daily,
                    [DigestTriggersEnum.weekEmailDigestEnabled]: false,
                };

                case StreamDigestChannelsEnum.WEEKLY: // stream weekly notification
                    return {
                    [DigestTriggersEnum.weekEmailDigestEnabled]: bothActive ? true : !weekly,
                    [DigestTriggersEnum.followerEmailDigestEnabled]: false,
                };

                case StreamDigestChannelsEnum.BOTH: // stream both notification
                    return {
                    [DigestTriggersEnum.followerEmailDigestEnabled]: !bothActive,
                    [DigestTriggersEnum.weekEmailDigestEnabled]: !bothActive,
                };

                case DigestTriggersEnum.emailDigestEnabled: // email notification
                    return {
                    [DigestTriggersEnum.emailDigestEnabled]: !notifications[DigestTriggersEnum.emailDigestEnabled],
                };

                default:
                    return {};
        }
    };

    // updated transactional settings
    const getUpdatedTransactionalSettings = (channel: TransactionalChannelsEnum, transactionalTrigger: TransactionalTriggersEnum) => {
        const bothActive = notifications[TransactionalChannelsEnum.EMAIL][transactionalTrigger] && notifications[TransactionalChannelsEnum.PUSH][transactionalTrigger];

        switch (channel) {
                case TransactionalChannelsEnum.EMAIL: // email notification
                 return {
                    [TransactionalChannelsEnum.EMAIL]: {
                        ...notifications[TransactionalChannelsEnum.EMAIL],
                      [transactionalTrigger]: bothActive ? true : !notifications[TransactionalChannelsEnum.EMAIL][transactionalTrigger],
                    },
                    [TransactionalChannelsEnum.PUSH]: {
                       ...notifications[TransactionalChannelsEnum.PUSH],
                      [transactionalTrigger]: false,
                    }
                };

                case TransactionalChannelsEnum.PUSH: // push notification
                return {
                    [TransactionalChannelsEnum.PUSH]: {
                        ...notifications[TransactionalChannelsEnum.PUSH],
                      [transactionalTrigger]: bothActive ? true : !notifications[TransactionalChannelsEnum.PUSH][transactionalTrigger],
                    },
                    [TransactionalChannelsEnum.EMAIL]: {
                       ...notifications[TransactionalChannelsEnum.EMAIL],
                      [transactionalTrigger]: false,
                    }
                };

                case TransactionalChannelsEnum.BOTH: // both notification
                return {
                    [TransactionalChannelsEnum.EMAIL]: {
                       ...notifications[TransactionalChannelsEnum.EMAIL],
                      [transactionalTrigger]: !bothActive,
                    },
                     [TransactionalChannelsEnum.PUSH]: {
                        ...notifications[TransactionalChannelsEnum.PUSH],
                      [transactionalTrigger]: !bothActive,
                    },
                };

                default:
                    return {};
        }
    }

    // save changes
    const handleChange = (data: TransactionalChannelsEnum | StreamDigestChannelsEnum | DigestTriggersEnum, transactionalType?: TransactionalTriggersEnum) => {
        let updatedDigest = {}; 
        let updatedTransactional = {};
       
        if(data && !transactionalType){
           updatedDigest = getUpdatedDigestSettings(data as StreamDigestChannelsEnum | DigestTriggersEnum);
        }

        if(data && transactionalType){
          updatedTransactional = getUpdatedTransactionalSettings(data as TransactionalChannelsEnum, transactionalType);
        }

        const body = {
            ...notifications,
            ...( _.size(updatedDigest) > 0 && updatedDigest),
            ...( _.size(updatedTransactional) > 0 && updatedTransactional),
        }

        notificationsMutate(body); // api call
     }

    return (
        <>
     { !isLoadingNotifications ? 
         <div className={styles.container}>
            <div className={styles.header}>
                <h6 className={styles.title}>Notification</h6>
                <p className={styles.subText}>Stay informed when your content gets attention.</p>
            </div>
            <div className={styles.contentWrapper}>
               <div className={styles.innerContainer}>
                       <TransactionalHeaders 
                          title="Digest Settings" 
                          subTitle="Choose how you receive updates about content engagement."
                       />
                       <div className={cx(styles.transactionalCardsContainer, { 
                            [styles.pointerEventNone]: updateNotificationPending || isNotificationsFetching
                        })}>
                           <TransactionalCards title="Stream Digest" subTitle="Get daily summaries of all content activity.">
                             <SegmentButton listOfSegments={streamDigestChannels} selected={selectedStreamDigestChannel()} onChange={(val) => handleChange(val as StreamDigestChannelsEnum)} />
                           </TransactionalCards>
                           <TransactionalCards title="Email Daily Digest" subTitle="Receive a daily email with all content interactions." classnames={styles.item} handleClick={() => handleChange(DigestTriggersEnum.emailDigestEnabled)}>
                               <SwitchButton
                                        id={'emailDigestEnabled'}
                                        checked={notifications.emailDigestEnabled}
                                />
                           </TransactionalCards>
                       </div>
               </div>

              <div className={styles.innerContainer}>
                    <TransactionalHeaders 
                          title="Transactional Triggers" 
                          subTitle="Setup transactional notification triggers for browser based push notification and/or emails."
                    />
                    <div className={cx(styles.transactionalTriggers, {
                        [styles.pointerEventNone]: updateNotificationPending || isNotificationsFetching,
                    })}>
                            {
                                _.map(TransactionalTriggersLabels, 
                                    (item: {title: string, subText: string} , key: TransactionalTriggersEnum) => (
                                            <TransactionalCards 
                                              key={key}
                                                title={item.title}
                                                subTitle={item.subText}
                                                classnames={styles.transactionalContent}
                                            >
                                                <SegmentButton listOfSegments={transactionalChannels} selected={selectedTransactionalChannel(key)} onChange={(channel) => handleChange(channel as TransactionalChannelsEnum, key) }/>
                                            </TransactionalCards>
                                    ))
                            }
                    </div>
               </div>
            </div>
           
        </div>  : 
      <div className={cx(styles.loading)}>
            <p>Loading...</p>
       </div>
    }
    </>
    )
}

export const TransactionalCards = ({title, subTitle, classnames, children , handleClick}: TransactionalCardsProps) => {
    return (
        <div className={cx(styles.transactionalCard, classnames)} onClick={handleClick && handleClick}>
                    <div className={styles.content}>
                            <h6 className={styles.title}> {title}</h6>
                            <p  className={styles.subTitle}>{subTitle}</p>
                    </div>
                    {children && children}
         </div>
    )
}

export const TransactionalHeaders = ({title, subTitle, classnames}: TransactionalHeaderProps) => {
    return (
        <div className={cx(styles.transactionalHeaders, classnames)}>
              <h6 className={styles.contentTitle}>{title}</h6>
             <p className={styles.contentSubText}>{subTitle}</p>
         </div>
    )
}

export default Notifications;